import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { LOCALE_TYPE } from "src/constants";
import { localeApi } from "src/services/api";
import {
  getSelectedLocale,
  markSessionIsReloading,
  markSessionReuseUnblocked,
  setSelectedLocale,
} from "src/utils/storage-utils";
import { useDispatch } from "react-redux";
import { searchDataActions } from "src/store/search";
import { useTenantContext } from "./tenant-context";
import { useLocation, useNavigate } from "react-router-dom";

const CURRENCY_SYMBOLS = {
  EUR: "€",
  USD: "$",
  PLN: "zł",
  GBP: "£",
  RUB: "₽",
};

const LANG_VALUES = {
  [LOCALE_TYPE.GB]: ["English", "Latvian", "Russian", "Lithuanian"],
  [LOCALE_TYPE.LV]: ["Angļu", "Latviešu", "Krievu", "Lietuviešu"],
  [LOCALE_TYPE.LT]: ["Anglų", "Latvių", "Rusų", "Lietuvių"],
  [LOCALE_TYPE.RU]: ["Английский", "Латышский", "Русский", "Литовский"],
};

const LocaleContext = createContext({
  currentLocale: "",
  currentLanguage: "",
  currency: "",
  currencySymbol: "",
  getStringRes: () => {},
  setSiteLanguage: () => {},
  languages: [],
  currencies: [],
});

const LANG_MAP = { en: "en_GB", lv: "lv_LV", ru: "ru_RU", lt: "lt_LT" };

function useLanguageQueryParam({ siteLanguages }) {
  const navigate = useNavigate();
  const location = useLocation();
  const sparams = new URLSearchParams(location.search);
  const langParam = sparams.get("lang");

  const onChangeSiteLanguage = useCallback(
    (lang) => {
      let sq = new URLSearchParams(location.search);
      sq.set("lang", lang);
      markSessionReuseUnblocked(); // this will allow booking to be reloaded without restarting booking
      markSessionIsReloading(); // this will allow booking to be reloaded without restarting booking
      if (LANG_MAP[lang]) {
        setSelectedLocale(LANG_MAP[lang]);
        document.documentElement.lang = lang;
      }
      navigate(`${location.pathname}?${sq.toString()}${location.hash || ""}`, {
        state: location.state,
        replace: true,
      });
    },
    [location.search, location.pathname, location.hash, location.state, navigate]
  );

  useEffect(() => {
    let lang = langParam;
    console.log("hook lang is", langParam);
    if (!langParam || !LANG_MAP[langParam] || !siteLanguages.includes(LANG_MAP[langParam])) {
      let defaultLocale = getSelectedLocale();
      if (!siteLanguages.includes(defaultLocale)) {
        defaultLocale = siteLanguages[0];
        setSelectedLocale(defaultLocale);
      }
      lang = defaultLocale.split("_")[0];
    }
    if (document.documentElement.lang !== lang) {
      document.documentElement.lang = lang;
    }
    if (langParam !== lang) {
      onChangeSiteLanguage(lang);
    }
  }, [langParam, onChangeSiteLanguage, siteLanguages]);

  return [LANG_MAP[langParam] || getSelectedLocale(), onChangeSiteLanguage];
}

export const LocaleContextManager = (props) => {
  const dispatch = useDispatch();
  const { currency, languages } = useTenantContext();
  const [locale, onChangeLocale] = useLanguageQueryParam({ siteLanguages: languages });
  const [state, setState] = useState({
    currentLocale: undefined,
    currentLanguage: undefined,
    currency: currency || "EUR",
    currencySymbol: CURRENCY_SYMBOLS.EUR,
    stringRes: {},
    languages: [
      { value: LOCALE_TYPE.GB, label: "English" },
      { value: LOCALE_TYPE.LV, label: "Latvian" },
      { value: LOCALE_TYPE.RU, label: "Russian" },
      { value: LOCALE_TYPE.LT, label: "Lithuanian" },
    ].filter((l) => languages.includes(l.value)),
    currencies: [{ value: "eur", label: "Euro - EUR" }],
  });

  const getStringRes = useCallback((key) => state.stringRes[key] || key, [state]);

  useEffect(() => {
    if (!state.currentLocale || locale !== state.currentLocale) {
      const lang = locale.split("_")[0];
      let stringRes = {};
      localeApi
        .getStringResources(lang)
        .then((data) => {
          stringRes = data.data;
        })
        .catch(console.error)
        .finally(() => {
          const labels = LANG_VALUES[locale];
          const siteLanguages = [
            { value: LOCALE_TYPE.GB, label: labels[0] },
            { value: LOCALE_TYPE.LV, label: labels[1] },
            { value: LOCALE_TYPE.RU, label: labels[2] },
            { value: LOCALE_TYPE.LT, label: labels[3] },
          ].filter((l) => languages.includes(l.value));

          setState((prev) => {
            return {
              ...prev,
              currentLocale: locale,
              currentLanguage: lang,
              stringRes: stringRes,
              languages: siteLanguages,
            };
          });
          setSelectedLocale(locale);
          dispatch(searchDataActions.changeCityDisplayLanguage(lang.toUpperCase()));
          document.title = stringRes["site.title"];
        });
    }
  }, [dispatch, languages, locale, state.currentLocale]);

  return (
    <LocaleContext.Provider
      value={{ ...state, getStringRes, setSiteLanguage: onChangeLocale }}>
      {state.currentLocale && props.children}
    </LocaleContext.Provider>
  );
};

export default LocaleContext;

export const useLocaleContext = () => useContext(LocaleContext);
